import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEquipment,
  fetchBrands,
  fetchCategories,
  createEquipment,
  updateEquipment,
  deleteEquipment,
} from "../redux/slices/equipmentSlice";
import { fetchParts } from "../redux/slices/partsSlice";
import {
  fetchTemplates,
  createTemplateFromEquipment,
  deleteTemplate,
} from "../redux/slices/equipmentTemplatesSlice";
import EquipmentTable from "../components/Tables/EquipmentTable";
import {
  Modal,
  LoadingSpinner,
  PartSelector,
  PartsListTable,
  TemplateSelector,
  StatsCard,
  EmptyState,
  DatalistInput,
} from "../components/Common";
import { useSearch } from "../context/SearchContext";
import { erpFailure } from "../utils/erpBooking";
import { useLanguage } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { printTable } from "../utils/printTable";

const Equipment = () => {
  const dispatch = useDispatch();
  const { searchTerm } = useSearch();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Redux state
  const {
    items: equipment,
    brands,
    categories,
    loading,
  } = useSelector((state) => state.equipment);
  const { items: parts } = useSelector((state) => state.parts);
  const { items: templates, loading: templatesLoading } = useSelector(
    (state) => state.equipmentTemplates,
  );

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);

  // State
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  // Filters
  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterWeek, setFilterWeek] = useState("");

  // Form
  const initialFormData = {
    model: "",
    brand: "",
    category: "",
    quantity: 1,
    serial_numbers: [""],
    article_id: "",
    year_manufactured: new Date().getFullYear(),
    production_date: "",
    status: "active",
    template_id: "",
    parts: [],
  };
  const [formData, setFormData] = useState(initialFormData);

  // Load data
  const loadData = useCallback(() => {
    dispatch(fetchEquipment());
  }, [dispatch]);

  useEffect(() => {
    loadData();
    dispatch(fetchBrands());
    dispatch(fetchCategories());
    dispatch(fetchParts());
    dispatch(fetchTemplates());
  }, [dispatch, loadData]);

  // Ctrl+P (Cmd+P on Mac) prints the filtered list as a plain table instead
  // of the browser printing the whole page. Goes through a ref because
  // handlePrint is defined further down and changes with the filters.
  const printListRef = useRef(null);
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p" && printListRef.current) {
        e.preventDefault();
        printListRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Helpers
  const getWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  };

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const currentWeekNumber = getWeekNumber(currentDate);

  // Filter logic
  const filteredEquipment = equipment.filter((item) => {
    const matchesSearch =
      item.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.article_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.created_from_template?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    const itemDate = new Date(item.created_at);
    if (filterYear && itemDate.getFullYear().toString() !== filterYear)
      return false;
    if (filterMonth && (itemDate.getMonth() + 1).toString() !== filterMonth)
      return false;

    if (filterWeek) {
      const itemWeek = getWeekNumber(itemDate);
      if (
        filterWeek === "current" &&
        (itemWeek !== currentWeekNumber ||
          itemDate.getFullYear() !== currentYear)
      )
        return false;
      if (filterWeek === "last") {
        // Week (and year) of the date 7 days ago, so "last week" in early
        // January doesn't also match that week number in older years.
        const weekAgo = new Date(currentDate);
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (
          itemWeek !== getWeekNumber(weekAgo) ||
          itemDate.getFullYear() !== weekAgo.getFullYear()
        )
          return false;
      }
      if (
        !["current", "last"].includes(filterWeek) &&
        itemWeek.toString() !== filterWeek
      )
        return false;
    }

    return true;
  });

  const handlePrint = () => {
    printTable({
      title: t("equipment.title"),
      columns: [
        { header: t("equipmentTable.colModel") },
        { header: t("equipmentTable.colSerialNumber") },
        { header: t("equipmentTable.colArticleId") },
        { header: t("equipmentTable.colCategory") },
        { header: t("equipmentTable.colBrand") },
        { header: t("equipmentTable.colCreatedBy") },
        { header: t("equipmentTable.colDateCreated") },
      ],
      rows: filteredEquipment.map((item) => [
        item.model,
        item.serial_number,
        item.article_id,
        item.category,
        item.brand,
        item.created_by_name?.trim() || item.created_by_username?.trim(),
        item.created_at
          ? new Date(item.created_at).toLocaleDateString("de-CH")
          : "",
      ]),
    });
  };
  printListRef.current = handlePrint;

  // Stats
  const stats = {
    total: equipment.length,
    thisWeek: equipment.filter((i) => {
      const d = new Date(i.created_at);
      return (
        getWeekNumber(d) === currentWeekNumber &&
        d.getFullYear() === currentYear
      );
    }).length,
    thisMonth: equipment.filter((i) => {
      const d = new Date(i.created_at);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).length,
    thisYear: equipment.filter(
      (i) => new Date(i.created_at).getFullYear() === currentYear,
    ).length,
    filtered: filteredEquipment.length,
  };

  // Handlers
  const resetForm = () => {
    setFormData(initialFormData);
    setSaveAsTemplate(false);
  };

  const handleTemplateSelect = (templateId) => {
    if (templateId) {
      const template = templates.find((t) => t.id === parseInt(templateId));
      if (template) {
        setFormData({
          ...formData,
          template_id: templateId,
          model: template.name,
          brand: template.brand || "",
          category: template.category || "",
          parts: [],
        });
      }
    } else {
      setFormData({ ...formData, template_id: "", parts: [] });
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!formData.template_id && formData.parts.length === 0) {
      alert(t("equipment.selectTemplateOrAddPart"));
      return;
    }

    try {
      const payload = {
        model: formData.model,
        serial_numbers: formData.serial_numbers.map((s) => s.trim() || null),
        brand: formData.brand || null,
        category: formData.category || null,
        article_id: formData.article_id || null,
        template_id: formData.template_id || null,
        production_date: formData.production_date || null,
        year_manufactured: formData.year_manufactured || null,
        parts: formData.parts.map((p) => ({
          part_id: p.part_id,
          quantity_needed: p.quantity_needed,
          notes: p.notes || "",
          part_name: p.part_name,
          part_color: p.part_color,
          part_sku: p.part_sku,
        })),
        save_as_template: saveAsTemplate,
        template_name: saveAsTemplate ? formData.model : null,
      };

      console.log("Creating equipment with payload:", payload); // Debug

      // Not .unwrap(): that would drop action.meta, which carries the ERP outcome.
      const action = await dispatch(createEquipment(payload));
      if (createEquipment.rejected.match(action)) {
        throw action.payload ?? action.error?.message;
      }
      const erpProblem = erpFailure(action.meta?.erp);
      if (erpProblem) alert(t("equipment.erpBookingFailed", erpProblem));

      setShowCreateModal(false);
      resetForm();
      loadData();
      if (saveAsTemplate) dispatch(fetchTemplates());
    } catch (err) {
      alert(t("equipment.failedToCreateEquipment", { message: err }));
    }
  };

  const handleEdit = async (id, data) => {
    try {
      await dispatch(updateEquipment({ id, data })).unwrap();
      loadData();
    } catch (err) {
      alert(t("equipment.failedToUpdate", { message: err.message }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("equipment.confirmDeleteEquipment")))
      return;
    try {
      await dispatch(deleteEquipment(id)).unwrap();
      loadData();
    } catch (err) {
      alert(t("equipment.failedToDelete", { message: err.message }));
    }
  };

  const handleViewDetails = async (item) => {
    try {
      const { equipmentAPI } = await import("../api/api");
      const response = await equipmentAPI.getById(item.id);
      setSelectedEquipment(response.data.success ? response.data.data : item);
    } catch {
      setSelectedEquipment(item);
    }
    setShowDetailsModal(true);
  };

  const handleSaveAsTemplate = (item) => {
    setSelectedEquipment(item);
    setTemplateName(item.model || "");
    setShowSaveTemplateModal(true);
  };

  const confirmSaveTemplate = async () => {
    if (!templateName.trim()) {
      alert(t("equipment.enterTemplateName"));
      return;
    }
    try {
      await dispatch(
        createTemplateFromEquipment({
          equipment_id: selectedEquipment.id,
          name: templateName.trim(),
        }),
      ).unwrap();
      alert(t("equipment.templateSaved", { name: templateName }));
      setShowSaveTemplateModal(false);
      setTemplateName("");
    } catch (err) {
      alert(t("equipment.failedToSaveTemplate", { message: err }));
    }
  };

  console.log(selectedEquipment, "selectedEquipment");
  const handleDeleteTemplate = async (template) => {
    if (window.confirm(t("equipment.confirmDeleteTemplate", { name: template.name }))) {
      try {
        await dispatch(deleteTemplate(template.id)).unwrap();
      } catch (err) {
        alert(t("equipment.failedToDeleteTemplate", { message: err }));
      }
    }
  };

  const handleQuantityChange = (rawValue) => {
    const quantity = Math.max(1, parseInt(rawValue, 10) || 1);
    setFormData((prev) => {
      const serial_numbers = prev.serial_numbers.slice(0, quantity);
      while (serial_numbers.length < quantity) serial_numbers.push("");
      return { ...prev, quantity, serial_numbers };
    });
  };

  const addSerialNumberField = () => {
    setFormData((prev) => ({
      ...prev,
      quantity: prev.quantity + 1,
      serial_numbers: [...prev.serial_numbers, ""],
    }));
  };

  const removeSerialNumberField = (index) => {
    setFormData((prev) => {
      if (prev.serial_numbers.length <= 1) return prev;
      const serial_numbers = prev.serial_numbers.filter((_, i) => i !== index);
      return { ...prev, quantity: serial_numbers.length, serial_numbers };
    });
  };

  const updateSerialNumber = (index, value) => {
    setFormData((prev) => {
      const serial_numbers = [...prev.serial_numbers];
      serial_numbers[index] = value;
      return { ...prev, serial_numbers };
    });
  };

  const addPart = (part) => {
    setFormData({ ...formData, parts: [...formData.parts, part] });
  };

  const removePart = (index) => {
    setFormData({
      ...formData,
      parts: formData.parts.filter((_, i) => i !== index),
    });
  };

  // Filter options
  const months = [
    { value: "1", label: t("equipment.months.1") },
    { value: "2", label: t("equipment.months.2") },
    { value: "3", label: t("equipment.months.3") },
    { value: "4", label: t("equipment.months.4") },
    { value: "5", label: t("equipment.months.5") },
    { value: "6", label: t("equipment.months.6") },
    { value: "7", label: t("equipment.months.7") },
    { value: "8", label: t("equipment.months.8") },
    { value: "9", label: t("equipment.months.9") },
    { value: "10", label: t("equipment.months.10") },
    { value: "11", label: t("equipment.months.11") },
    { value: "12", label: t("equipment.months.12") },
  ];

  const availableYears = [
    ...new Set(equipment.map((i) => new Date(i.created_at).getFullYear())),
  ].sort((a, b) => b - a);

  const weekOptions = [
    { value: "current", label: t("equipment.thisWeek") },
    { value: "last", label: t("equipment.lastWeek") },
    ...[...Array(52)].map((_, i) => ({
      value: (i + 1).toString(),
      label: t("equipment.week", { number: i + 1 }),
    })),
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t("equipment.title")}</h1>
          <p className="text-gray-600 mt-1">{t("equipment.subtitle")}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate("/equipment/report")}
            className="btn-secondary"
          >
            📊 {t("equipment.report")}
          </button>
          <button
            onClick={() => setShowTemplateModal(true)}
            className="btn-secondary"
          >
            📋 {t("equipment.templatesCount", { count: templates.length })}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            {t("equipment.createEquipment")}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
        <StatsCard label={t("equipment.totalEquipment")} value={stats.total} color="gray" />
        <StatsCard
          label={t("equipment.createdThisWeek")}
          value={stats.thisWeek}
          color="orange"
        />
        <StatsCard
          label={t("equipment.createdThisMonth")}
          value={stats.thisMonth}
          color="green"
        />
        <StatsCard
          label={t("equipment.createdThisYear")}
          value={stats.thisYear}
          color="blue"
        />
        <StatsCard
          label={t("equipment.filteredResults")}
          value={stats.filtered}
          color="purple"
        />
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 gap-4">
          <div className="flex-1">
            {searchTerm && (
              <p className="text-sm text-gray-600">
                {t("equipment.searchingFor")}{" "}
                <span className="font-medium">"{searchTerm}"</span>
              </p>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={filterWeek}
              onChange={(e) => setFilterWeek(e.target.value)}
              className="input-field"
            >
              <option value="">{t("equipment.allWeeks")}</option>
              {weekOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="input-field"
            >
              <option value="">{t("equipment.allMonths")}</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="input-field"
            >
              <option value="">{t("equipment.allYears")}</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            {(filterYear || filterMonth || filterWeek) && (
              <button
                onClick={() => {
                  setFilterYear("");
                  setFilterMonth("");
                  setFilterWeek("");
                }}
                className="btn-secondary"
              >
                {t("equipment.clear")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : filteredEquipment.length === 0 ? (
        <EmptyState
          icon="🔧"
          title={t("equipment.noEquipmentYet")}
          message={t("equipment.createFirstEquipment")}
          actionLabel={t("equipment.createEquipment")}
          onAction={() => setShowCreateModal(true)}
          hasItems={equipment.length > 0}
        />
      ) : (
        <EquipmentTable
          equipment={filteredEquipment}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewDetails={handleViewDetails}
          onSaveAsTemplate={handleSaveAsTemplate}
        />
      )}

      {/* Templates Modal */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title={t("equipment.equipmentTemplates")}
        size="lg"
      >
        {templatesLoading ? (
          <LoadingSpinner />
        ) : templates.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-gray-500">{t("equipment.noTemplatesSaved")}</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {templates.map((tpl) => (
              <div
                key={tpl.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
              >
                <div>
                  <p className="font-medium">{tpl.name}</p>
                  <p className="text-sm text-gray-500">
                    {t("equipment.partsCount", { count: tpl.parts_data?.length || 0 })}{" "}
                    {tpl.brand && `• ${tpl.brand}`}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      handleTemplateSelect(tpl.id.toString());
                      setShowTemplateModal(false);
                      setShowCreateModal(true);
                    }}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm"
                  >
                    {t("equipment.use")}
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(tpl)}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm"
                  >
                    {t("common.delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Save Template Modal */}
      <Modal
        isOpen={showSaveTemplateModal}
        onClose={() => setShowSaveTemplateModal(false)}
        title={t("equipment.saveAsTemplateTitle")}
        size="sm"
      >
        <div className="space-y-4">
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="input-field"
            placeholder={t("equipment.templateNamePlaceholder")}
          />
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowSaveTemplateModal(false)}
              className="btn-secondary"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={confirmSaveTemplate}
              className="btn-primary"
              disabled={!templateName.trim()}
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title={t("equipment.createEquipmentModalTitle")}
        size="lg"
      >
        <form onSubmit={handleCreate} className="space-y-6">
          {/* Template Selector */}
          <TemplateSelector
            templates={templates}
            value={formData.template_id}
            onChange={handleTemplateSelect}
          />

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipment.model")} *
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) =>
                  setFormData({ ...formData, model: e.target.value })
                }
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipment.quantity")}
              </label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                className="input-field"
              />
            </div>
            <DatalistInput
              label={t("equipment.brand")}
              value={formData.brand}
              onChange={(v) => setFormData({ ...formData, brand: v })}
              options={brands}
            />
            <DatalistInput
              label={t("equipment.category")}
              value={formData.category}
              onChange={(v) => setFormData({ ...formData, category: v })}
              options={categories}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipment.articleId")}
              </label>
              <input
                type="text"
                value={formData.article_id}
                onChange={(e) =>
                  setFormData({ ...formData, article_id: e.target.value })
                }
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("equipment.productionDate")}
              </label>
              <input
                type="date"
                value={formData.production_date}
                onChange={(e) =>
                  setFormData({ ...formData, production_date: e.target.value })
                }
                className="input-field"
              />
            </div>
          </div>

          {/* Serial Numbers (one row per unit, count driven by Quantity) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                {t("equipment.serialNumbers")}
              </label>
              <button
                type="button"
                onClick={addSerialNumberField}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                + {t("equipment.addSerialNumber")}
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {formData.serial_numbers.map((serial, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className="text-sm text-gray-400 w-6 text-right">
                    {index + 1}.
                  </span>
                  <input
                    type="text"
                    value={serial}
                    onChange={(e) => updateSerialNumber(index, e.target.value)}
                    className="input-field flex-1"
                    placeholder={t("equipment.serialNumberOptional")}
                  />
                  <button
                    type="button"
                    onClick={() => removeSerialNumberField(index)}
                    disabled={formData.serial_numbers.length <= 1}
                    className="px-2 py-1 text-red-600 hover:text-red-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                    aria-label={t("equipment.removeSerialNumber")}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Parts (only if no template) */}
          {!formData.template_id && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                {t("equipment.partsWithCount", { count: formData.parts.length })}
              </h3>
              <PartSelector
                parts={parts}
                onAdd={addPart}
                existingParts={formData.parts}
              />
              <div className="mt-4">
                <PartsListTable parts={formData.parts} onRemove={removePart} />
              </div>
            </div>
          )}

          {/* Save as Template Checkbox */}
          {!formData.template_id && formData.parts.length > 0 && (
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={saveAsTemplate}
                onChange={(e) => setSaveAsTemplate(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">{t("equipment.saveAsTemplateCheckbox")}</span>
            </label>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              className="btn-secondary"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!formData.template_id && formData.parts.length === 0}
            >
              {formData.quantity > 1
                ? t("equipment.createMultiple", { count: formData.quantity })
                : formData.template_id
                  ? t("equipment.createFromTemplate")
                  : t("equipment.createWithPartsCount", { count: formData.parts.length })}
            </button>
          </div>
        </form>
      </Modal>

      {/* Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={t("equipment.equipmentDetailsTitle")}
        size="lg"
      >
        {selectedEquipment && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">{t("equipment.model")}</p>
                <p className="text-lg font-semibold">
                  {selectedEquipment.model}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t("equipment.serial")}</p>
                <p className="text-lg font-semibold">
                  {selectedEquipment.serial_number || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t("equipment.brand")}</p>
                <p className="text-lg font-semibold">
                  {selectedEquipment.brand || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t("equipment.category")}</p>
                <p className="text-lg font-semibold">
                  {selectedEquipment.category || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t("equipment.template")}</p>
                <p className="text-lg font-semibold">
                  {selectedEquipment.created_from_template || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t("equipment.created")}</p>
                <p className="text-lg font-semibold">
                  {selectedEquipment.created_at
                    ? new Date(
                        selectedEquipment.created_at,
                      ).toLocaleDateString()
                    : "-"}
                </p>
              </div>
            </div>

            {selectedEquipment.parts?.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">{t("equipment.partsUsed")}</h3>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleSaveAsTemplate(selectedEquipment);
                    }}
                    className="btn-secondary text-sm"
                  >
                    {t("equipment.saveAsTemplate")}
                  </button>
                </div>
                <PartsListTable
                  parts={selectedEquipment.parts}
                  showRemove={false}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Equipment;
